<template>
    <section class="discount-coupons">
        <header class="discount-coupons__header">
            <div>
                <h2 class="discount-coupons__title">Cupons de Desconto</h2>
                <p class="discount-coupons__description">
                    Crie e gerencie cupons para oferecer descontos na sua plataforma.
                </p>
            </div>
            <button type="button" class="discount-coupons__create" @click="openNewCuponModal">
                <fa icon="fa-plus" />
                Novo Cupom
            </button>
        </header>

        <div v-if="loading" class="discount-coupons__empty">
            <h2>Carregando cupons</h2>
            <p>Aguarde um instante.</p>
        </div>

        <div v-else-if="loadError" class="discount-coupons__empty">
            <h2>Não foi possível carregar os cupons</h2>
            <p>Tente novamente em instantes.</p>
            <button type="button" class="discount-coupons__create" @click="getCupons">
                Tentar novamente
            </button>
        </div>

        <div v-else-if="!cupons.length" class="discount-coupons__empty">
            <h2>Nenhum cupom cadastrado</h2>
            <p>Crie seu primeiro cupom para oferecer descontos aos seus clientes.</p>
            <button type="button" class="discount-coupons__create" @click="openNewCuponModal">
                <fa icon="fa-plus" />
                Criar primeiro cupom
            </button>
        </div>

        <div v-else class="discount-coupons__table-wrapper">
            <table class="discount-coupons__table">
                <thead>
                    <tr>
                        <th>Código</th>
                        <th>Desconto</th>
                        <th>Validade</th>
                        <th>Status</th>
                        <th>Ações</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="cupom in cupons" :key="cupom.id || cupom.code">
                        <td data-label="Código">
                            <div class="discount-coupons__code-cell">
                                <span class="discount-coupons__icon" aria-hidden="true">
                                    <fa icon="fa-tag" />
                                </span>
                                <span class="discount-coupons__code">{{ cupom.code }}</span>
                            </div>
                        </td>
                        <td data-label="Desconto">{{ cupom.discount }}%</td>
                        <td data-label="Validade">{{ formatDate(cupom.expired) }}</td>
                        <td data-label="Status">
                            <span
                                class="discount-coupons__badge"
                                :class="'discount-coupons__badge--' + couponStatus(cupom)"
                            >
                                {{ couponStatusLabel(cupom) }}
                            </span>
                        </td>
                        <td data-label="Ações">
                            <div class="discount-coupons__actions">
                                <v-tooltip text="Editar" location="top">
                                    <template v-slot:activator="{ props }">
                                        <button
                                            v-bind="props"
                                            type="button"
                                            class="discount-coupons__action"
                                            aria-label="Editar"
                                            @click="openEditCuponModal(cupom)"
                                        >
                                            <fa icon="fa-pencil" />
                                        </button>
                                    </template>
                                </v-tooltip>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="discount-coupons__pagination" v-if="!loading && totalPages > 1">
            <button type="button" :disabled="page === 0" @click="changePage(page - 1)">
                Anterior
            </button>
            <span>Página {{ page + 1 }} de {{ totalPages }}</span>
            <button
                type="button"
                :disabled="page >= totalPages - 1"
                @click="changePage(page + 1)"
            >
                Próxima
            </button>
        </div>
    </section>

    <v-dialog
        v-model="isNewCuponModalOpen"
        max-width="720"
        :persistent="newCuponFormSaving"
    >
        <div class="plan-modal" role="dialog" aria-labelledby="new-coupon-title">
            <header class="plan-modal__header">
                <div>
                    <h2 id="new-coupon-title" class="plan-modal__title">Novo Cupom</h2>
                    <p class="plan-modal__subtitle">
                        Crie um cupom de desconto para seus clientes.
                    </p>
                </div>
                <button
                    type="button"
                    class="plan-modal__close"
                    :disabled="newCuponFormSaving"
                    aria-label="Fechar"
                    @click="closeNewCuponModal"
                >
                    <fa icon="fa-xmark" />
                </button>
            </header>
            <div class="plan-modal__body">
                <NewCuponForm
                    ref="newCuponFormRef"
                    layout="modal"
                    hide-heading
                    @saving-change="newCuponFormSaving = $event"
                    @success="onNewCuponSuccess"
                    @cancel="closeNewCuponModal"
                />
            </div>
        </div>
    </v-dialog>

    <v-dialog
        v-model="isEditCuponModalOpen"
        max-width="720"
        :persistent="editCuponFormSaving"
    >
        <div
            v-if="editingCuponId"
            class="plan-modal"
            role="dialog"
            aria-labelledby="edit-coupon-title"
        >
            <header class="plan-modal__header">
                <div>
                    <h2 id="edit-coupon-title" class="plan-modal__title">Editar Cupom</h2>
                    <p class="plan-modal__subtitle">
                        Atualize as informações do cupom.
                    </p>
                </div>
                <button
                    type="button"
                    class="plan-modal__close"
                    :disabled="editCuponFormSaving"
                    aria-label="Fechar"
                    @click="closeEditCuponModal"
                >
                    <fa icon="fa-xmark" />
                </button>
            </header>
            <div class="plan-modal__body">
                <EditCuponForm
                    :key="editingCuponId"
                    :cupom-id="editingCuponId"
                    layout="modal"
                    hide-heading
                    @saving-change="editCuponFormSaving = $event"
                    @success="onEditCuponSuccess"
                    @cancel="closeEditCuponModal"
                />
            </div>
        </div>
    </v-dialog>
</template>

<script>
    import Cupom from '@/utils/Weflix/cupom';
    import { mapState } from 'vuex';
    import { toast } from 'vue3-toastify';
    import NewCuponForm from '@/components/Weflix/Payment/NewCuponForm.vue';
    import EditCuponForm from '@/components/Weflix/Payment/EditCuponForm.vue';

    export default {
        data() {
            return {
                statusTrial: 'Desativado',
                isTrialActive: false,
                trialRange: [3, 3],
                modelValue: 0,
                cupons: [],
                isMobileIcon: false,
                page: 0,
                linesPerPage: 8,
                totalPages: 1,
                totalElements: 0,
                isNewCuponModalOpen: false,
                newCuponFormSaving: false,
                isEditCuponModalOpen: false,
                editingCuponId: null,
                editCuponFormSaving: false,
                loading: false,
                loadError: false,
            };
        },
        components: {
            NewCuponForm,
            EditCuponForm,
        },
        mounted() {
            this.checkScreenSize();
            window.addEventListener('resize', this.checkScreenSize);
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.checkScreenSize);
        },
        computed: {
            ...mapState(['token']),
        },
        methods: {
            openNewCuponModal() {
                this.isNewCuponModalOpen = true;
                this.$nextTick(() => {
                    this.$refs.newCuponFormRef?.resetForm?.();
                });
            },
            closeNewCuponModal() {
                if (this.newCuponFormSaving) return;
                this.isNewCuponModalOpen = false;
            },
            async onNewCuponSuccess() {
                this.isNewCuponModalOpen = false;
                await this.getCupons();
                this.notify('Cupom criado com sucesso!', 'success');
                this.$nextTick(() => {
                    this.$refs.newCuponFormRef?.resetForm?.();
                });
            },
            openEditCuponModal(cupom) {
                this.editingCuponId = cupom.id;
                this.isEditCuponModalOpen = true;
            },
            closeEditCuponModal() {
                if (this.editCuponFormSaving) return;
                this.isEditCuponModalOpen = false;
                this.$nextTick(() => {
                    this.editingCuponId = null;
                });
            },
            async onEditCuponSuccess() {
                this.isEditCuponModalOpen = false;
                this.$nextTick(() => {
                    this.editingCuponId = null;
                });
                await this.getCupons();
            },
            notify(message, type = 'info') {
                toast(message, {
                    autoClose: 3000,
                    position: 'top-right',
                    theme: 'light',
                    limit: 1,
                    type: type,
                    dangerouslyHTMLString: true,
                });
            },
            changeStatusTrial() {
                this.statusTrial = this.isTrialActive
                    ? 'Ativado'
                    : 'Desativado';
            },
            async getCupons() {
                this.loading = true;
                this.loadError = false;
                try {
                    const response = await Cupom.getCupons(
                        this.token,
                        this.page,
                        this.linesPerPage
                    );
                    this.cupons = response.content || [];
                    this.totalPages = response.totalPages || 1;
                    this.totalElements = response.totalElements || 0;
                } catch (error) {
                    console.error(error);
                    this.loadError = true;
                } finally {
                    this.loading = false;
                }
            },
            couponStatus(cupom) {
                const expDate = cupom.expired ? new Date(cupom.expired) : null;
                if (expDate && !Number.isNaN(expDate.getTime()) && new Date() > expDate) {
                    return 'expired';
                }
                return cupom.status == 1 ? 'active' : 'inactive';
            },
            couponStatusLabel(cupom) {
                const labels = {
                    active: 'Ativo',
                    inactive: 'Inativo',
                    expired: 'Expirado',
                };
                return labels[this.couponStatus(cupom)];
            },
            changePage(newPage) {
                if (newPage >= 0 && newPage < this.totalPages) {
                    this.page = newPage;
                    this.getCupons();
                }
            },
            formatDate(isoDate) {
                const date = new Date(isoDate);
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();

                return `${day}/${month}/${year}`;
            },
            isExpired(expirationDate, status) {
                const currentDate = new Date();
                const expDate = new Date(expirationDate);
                return status == 0 || currentDate > expDate;
            },
            checkScreenSize() {
                this.isMobileIcon = window.innerWidth <= 600;
            },
        },
        created() {
            this.getCupons();
        },
    };
</script>
